// 人脸优化：素颜不动；淡妆 = 匀肤 + 淡化瑕疵 + 一点气色；通勤妆 = 遮住痘痘斑点黑眼圈 + 补光。
// 只改颜色和明暗，不瘦脸、不大眼、不动任何像素的位置，五官和脸型始终是原图。

import { clamp, convexHull, dilate, fillPolygon, gaussianish, type Pt } from "./geometry";
import { ctx2d, maskToCanvas, newCanvas } from "./image";
import {
  type Analysis,
  faceBox,
  LEFT_BROW,
  LEFT_EYE,
  LEFT_LOWER_LID,
  LIPS,
  NOSTRILS,
  pick,
  RIGHT_BROW,
  RIGHT_EYE,
  RIGHT_LOWER_LID,
} from "./vision";

export type BeautyLevel = 0 | 1 | 2;

export const BEAUTY_LEVELS: { level: BeautyLevel; name: string; hint: string }[] = [
  { level: 0, name: "素颜", hint: "原图，不做任何美化" },
  { level: 1, name: "淡妆", hint: "匀肤、淡化瑕疵、提一点气色" },
  { level: 2, name: "通勤妆", hint: "遮住痘痘斑点和黑眼圈，补光" },
];

interface Params {
  /** 纹理去除比例（保留 1-x 的皮肤纹理，避免塑料感） */
  texture: number;
  /** 瑕疵遮盖（痘痘、痘印、斑点），1 = 完全遮住 */
  conceal: number;
  /** 局部匀肤（泛红、色块向周围肤色靠拢） */
  even: number;
  /** 提亮 */
  bright: number;
  /** 黑眼圈 */
  underEye: number;
  /** 补光：抹平脸上的阴影 */
  fill: number;
  /** 气色：一点点红润 */
  rosy: number;
  /** 引导滤波 eps（越大越平滑） */
  eps: number;
}

const PARAMS: Record<1 | 2, Params> = {
  1: { texture: 0.42, conceal: 0.85, even: 0.3, bright: 0.035, underEye: 0.45, fill: 0.18, rosy: 1.5, eps: 0.035 },
  2: { texture: 0.55, conceal: 1, even: 0.45, bright: 0.05, underEye: 0.7, fill: 0.35, rosy: 2.5, eps: 0.05 },
};

export function applyBeauty(base: HTMLCanvasElement, a: Analysis, level: BeautyLevel): HTMLCanvasElement {
  if (level === 0) return base;
  const p = PARAMS[level];
  const W = base.width;
  const H = base.height;
  const { w: mw, h: mh } = a.masks;
  const sx = mw / W;
  const sy = mh / H;

  // ---- 皮肤权重（脸 + 脖子），扣掉眼睛、眉毛、嘴唇、鼻孔 ----
  const skin = new Uint8Array(mw * mh);
  for (let i = 0; i < skin.length; i++) skin[i] = Math.max(a.masks.faceSkin[i], Math.round(a.masks.bodySkin[i] * 0.85));
  const protect = new Uint8Array(mw * mh);
  const underEye = new Uint8Array(mw * mh);
  let faceW = W * 0.35;
  if (a.landmarks) {
    const lm = a.landmarks;
    faceW = faceBox(lm).width;
    const toMask = (pts: Pt[]) => pts.map(([x, y]) => [x * sx, y * sy] as Pt);
    for (const group of [LEFT_EYE, RIGHT_EYE, LEFT_BROW, RIGHT_BROW, LIPS, NOSTRILS]) {
      fillPolygon(protect, mw, mh, convexHull(toMask(pick(lm, group))));
    }
    for (const lid of [LEFT_LOWER_LID, RIGHT_LOWER_LID]) {
      const pts = pick(lm, lid);
      const eyeW = Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]);
      const drop = eyeW * 0.42;
      const lower = pts.map(([x, y]) => [x, y + drop] as Pt).reverse();
      const upper = pts.map(([x, y]) => [x, y + eyeW * 0.08] as Pt);
      fillPolygon(underEye, mw, mh, toMask([...upper, ...lower]));
    }
  }
  const protectD = dilate(protect, mw, mh, Math.max(1, Math.round(faceW * sx * 0.02)));
  for (let i = 0; i < skin.length; i++) if (protectD[i]) skin[i] = 0;
  const skinSoft = gaussianish(skin, mw, mh, 2);
  const eyeSoft = gaussianish(underEye, mw, mh, Math.max(2, Math.round(faceW * sx * 0.03)));

  // 只处理皮肤所在的包围盒，省时间
  let bx0 = mw;
  let by0 = mh;
  let bx1 = -1;
  let by1 = -1;
  for (let y = 0; y < mh; y++)
    for (let x = 0; x < mw; x++)
      if (skinSoft[y * mw + x] > 8) {
        bx0 = Math.min(bx0, x);
        by0 = Math.min(by0, y);
        bx1 = Math.max(bx1, x);
        by1 = Math.max(by1, y);
      }
  const out = newCanvas(W, H);
  const og = ctx2d(out);
  og.drawImage(base, 0, 0);
  if (bx1 < 0) return out;

  const X0 = clamp(Math.floor(bx0 / sx) - 4, 0, W - 1);
  const Y0 = clamp(Math.floor(by0 / sy) - 4, 0, H - 1);
  const X1 = clamp(Math.ceil((bx1 + 1) / sx) + 4, 0, W);
  const Y1 = clamp(Math.ceil((by1 + 1) / sy) + 4, 0, H);
  const w = X1 - X0;
  const h = Y1 - Y0;
  const img = og.getImageData(X0, Y0, w, h);
  const d = img.data;

  // 权重图放大到处理区域
  const wSkin = regionMask(skinSoft, mw, mh, W, H, X0, Y0, w, h);
  const wEye = regionMask(eyeSoft, mw, mh, W, H, X0, Y0, w, h);
  const n = w * h;

  // ---- 分解：亮度 L、色度 (R-L, G-L, B-L) ----
  const L = new Float32Array(n);
  const CR = new Float32Array(n);
  const CG = new Float32Array(n);
  const CB = new Float32Array(n);
  const RED = new Float32Array(n);
  const WS = new Float32Array(n);
  for (let j = 0, i = 0; j < n; j++, i += 4) {
    const l = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    L[j] = l;
    CR[j] = d[i] - l;
    CG[j] = d[i + 1] - l;
    CB[j] = d[i + 2] - l;
    RED[j] = d[i] - (d[i + 1] + d[i + 2]) / 2;
    WS[j] = wSkin[j] / 255;
  }

  // 保边平滑的"底妆层"
  const r = Math.max(2, Math.round(faceW * 0.018));
  const S = guidedSelf(L, w, h, r, (p.eps * 255) ** 2);

  // 只在皮肤里取平均的局部肤色（不会被头发、嘴唇带偏）
  const rc = Math.max(3, Math.round(faceW * 0.035));
  const den = boxMean(WS, w, h, rc);
  const local = (src: Float32Array) => {
    const tmp = new Float32Array(n);
    for (let j = 0; j < n; j++) tmp[j] = src[j] * WS[j];
    const num = boxMean(tmp, w, h, rc);
    for (let j = 0; j < n; j++) num[j] = den[j] > 0.02 ? num[j] / den[j] : src[j];
    return num;
  };
  const LR = local(CR);
  const LG = local(CG);
  const LB = local(CB);
  const LRED = local(RED);

  // 补光用的大尺度光照
  const rl = Math.max(6, Math.round(faceW * 0.09));
  const denL = boxMean(WS, w, h, rl);
  const tmpL = new Float32Array(n);
  for (let j = 0; j < n; j++) tmpL[j] = S[j] * WS[j];
  const LIGHT = boxMean(tmpL, w, h, rl);
  for (let j = 0; j < n; j++) LIGHT[j] = denL[j] > 0.02 ? LIGHT[j] / denL[j] : S[j];

  // 脸部平均亮度（补光和黑眼圈的目标）
  let cl = 0;
  let cn = 0;
  for (let j = 0; j < n; j += 3) {
    if (WS[j] < 0.8) continue;
    cl += S[j];
    cn++;
  }
  cl = cn ? cl / cn : 150;

  // ---- 瑕疵图：比周围暗的小点（斑点、痘印）+ 比周围红的小点（痘痘、泛红） ----
  const spot = new Float32Array(n);
  for (let j = 0; j < n; j++) {
    if (WS[j] <= 0.004) continue;
    const dark = smoothstep(3, 14, S[j] - L[j]);
    const red = smoothstep(4, 16, RED[j] - LRED[j]);
    spot[j] = Math.max(dark, red);
  }
  // 瑕疵周围的晕圈也一起盖住
  const halo = boxMean(boxMean(spot, w, h, Math.max(1, Math.round(r * 0.6))), w, h, Math.max(1, Math.round(r * 0.6)));
  // 成片密集的小暗点是胡茬/毛发纹理，不是痘痘，少遮一点
  const dense = boxMean(spot, w, h, rc);

  for (let j = 0; j < n; j++) {
    const ws = WS[j];
    const we = wEye[j] / 255;
    if (ws <= 0.004 && we <= 0.004) continue;
    const i = j * 4;
    const l = L[j];
    const detail = l - S[j];
    const sp = Math.min(1, Math.max(spot[j], halo[j] * 1.6)) * p.conceal * (1 - smoothstep(0.25, 0.5, dense[j]));

    // 亮度：保留一部分纹理；瑕疵处直接换成底妆层
    let nl = l - detail * p.texture * ws;
    nl += (S[j] - nl) * sp * ws;
    // 补光：把阴影往脸部平均亮度拉，高光略压
    const lightDiff = cl - LIGHT[j];
    nl += lightDiff * p.fill * ws * (lightDiff > 0 ? 1 : 0.5);
    // 黑眼圈
    if (we > 0 && nl < cl) nl += (cl - nl) * p.underEye * we * ws;
    nl *= 1 + p.bright * ws;

    // 色度：局部匀肤 + 瑕疵处用周围肤色 + 一点气色
    const e = Math.min(1, p.even + sp) * ws;
    const cr = CR[j] + (LR[j] - CR[j]) * e + p.rosy * ws;
    const cg = CG[j] + (LG[j] - CG[j]) * e - p.rosy * 0.3 * ws;
    const cb = CB[j] + (LB[j] - CB[j]) * e - p.rosy * 0.4 * ws;
    d[i] = nl + cr;
    d[i + 1] = nl + cg;
    d[i + 2] = nl + cb;
  }
  og.putImageData(img, X0, Y0);
  return out;
}

function regionMask(
  m: Uint8Array,
  mw: number,
  mh: number,
  W: number,
  H: number,
  X0: number,
  Y0: number,
  w: number,
  h: number,
) {
  const full = maskToCanvas(m, mw, mh, W, H);
  const data = ctx2d(full).getImageData(X0, Y0, w, h).data;
  const out = new Uint8Array(w * h);
  for (let i = 0; i < out.length; i++) out[i] = data[i * 4 + 3];
  return out;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** 自引导滤波（He et al.），保边平滑：平坦处抹匀，五官轮廓处不糊 */
export function guidedSelf(I: Float32Array, w: number, h: number, r: number, eps: number): Float32Array {
  const n = w * h;
  const II = new Float32Array(n);
  for (let i = 0; i < n; i++) II[i] = I[i] * I[i];
  const mI = boxMean(I, w, h, r);
  const mII = boxMean(II, w, h, r);
  const A = new Float32Array(n);
  const B = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const v = mII[i] - mI[i] * mI[i];
    const a = v / (v + eps);
    A[i] = a;
    B[i] = mI[i] - a * mI[i];
  }
  const mA = boxMean(A, w, h, r);
  const mB = boxMean(B, w, h, r);
  const q = new Float32Array(n);
  for (let i = 0; i < n; i++) q[i] = mA[i] * I[i] + mB[i];
  return q;
}

export function boxMean(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let acc = 0;
    let cnt = 0;
    for (let x = 0; x <= Math.min(r, w - 1); x++) {
      acc += src[row + x];
      cnt++;
    }
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc / cnt;
      const add = x + r + 1;
      const sub = x - r;
      if (add < w) {
        acc += src[row + add];
        cnt++;
      }
      if (sub >= 0) {
        acc -= src[row + sub];
        cnt--;
      }
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    let cnt = 0;
    for (let y = 0; y <= Math.min(r, h - 1); y++) {
      acc += tmp[y * w + x];
      cnt++;
    }
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / cnt;
      const add = y + r + 1;
      const sub = y - r;
      if (add < h) {
        acc += tmp[add * w + x];
        cnt++;
      }
      if (sub >= 0) {
        acc -= tmp[sub * w + x];
        cnt--;
      }
    }
  }
  return out;
}

// ---------------- 整体光线（整张图，合成后再做，保证脸和脖子、背景一致） ----------------

export type Tone = [Uint8ClampedArray, Uint8ClampedArray, Uint8ClampedArray];

/** 根据脸部平均亮度算一组调色曲线：曝光向合适亮度靠、轻微对比、通勤妆略暖 */
export function toneFor(level: BeautyLevel, faceMean: number): Tone | null {
  if (level === 0) return null;
  const strength = level === 1 ? 0.4 : 0.75;
  const contrast = level === 1 ? 0.03 : 0.06;
  const m = clamp(faceMean / 255, 0.08, 0.95);
  const g = Math.log(0.62) / Math.log(m);
  const gamma = clamp(1 - (1 - g) * strength, 0.8, 1.1);
  const warm = level === 2 ? [1.012, 1, 0.988] : [1.004, 1, 0.996];
  return [0, 1, 2].map((c) => {
    const t = new Uint8ClampedArray(256);
    for (let v = 0; v < 256; v++) {
      let x = (v / 255) ** gamma;
      x = x + contrast * (x - 0.5) * (1 - Math.abs(2 * x - 1));
      t[v] = 255 * x * warm[c];
    }
    return t;
  }) as Tone;
}

export function applyTone(c: HTMLCanvasElement, tone: Tone | null) {
  if (!tone) return;
  const g = ctx2d(c);
  const img = g.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  const [tr, tg, tb] = tone;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = tr[d[i]];
    d[i + 1] = tg[d[i + 1]];
    d[i + 2] = tb[d[i + 2]];
  }
  g.putImageData(img, 0, 0);
}

/** 脸部皮肤平均亮度（0-255） */
export function faceLuma(base: HTMLCanvasElement, a: Analysis): number {
  const d = ctx2d(base).getImageData(0, 0, base.width, base.height).data;
  const { w: mw, h: mh, faceSkin } = a.masks;
  let sum = 0;
  let cnt = 0;
  for (let y = 0; y < base.height; y += 4) {
    const my = Math.min(mh - 1, Math.floor((y * mh) / base.height));
    for (let x = 0; x < base.width; x += 4) {
      const mx = Math.min(mw - 1, Math.floor((x * mw) / base.width));
      if (faceSkin[my * mw + mx] < 160) continue;
      const i = (y * base.width + x) * 4;
      sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      cnt++;
    }
  }
  return cnt ? sum / cnt : 150;
}
